import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";

/**
 * Shared read-only subscription/quota snapshot used by every UI-level
 * pre-check. Never mutates usage. Backend `/api/billing/subscription`
 * remains the source of truth; this hook just exposes its fields to
 * every component that needs to render quota-aware UI.
 */
export default function useBillingSubscription() {
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const res = await api.get("/billing/subscription");
      setSub(res.data || null);
    } catch {
      setSub(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);
  return { sub, loading, refresh };
}

// -- Helpers used across quota-aware UIs -----------------------------
export function publishUnitsNeeded(eligiblePlatforms) {
  return Math.max(1, (eligiblePlatforms || []).length || 1);
}

export function aiUnitsNeeded(targetPlatforms) {
  return Math.max(1, (targetPlatforms || []).length || 1);
}

/**
 * Return { blocked, remaining, needed, plan } describing whether the
 * multi-platform publish would fit within the monthly published-post
 * quota. `null` limit means unlimited (never blocked).
 */
export function evaluatePublishQuota(sub, needed) {
  const limit = sub?.monthly_limit;
  const remaining = sub?.monthly_remaining;
  if (limit == null || remaining == null) {
    return { blocked: false, remaining: null, needed, plan: sub?.effective_plan };
  }
  return {
    blocked: needed > remaining,
    remaining,
    needed,
    plan: sub?.effective_plan,
  };
}

export function evaluateAiQuota(sub, needed) {
  const limit = sub?.ai_generations_limit;
  const remaining = sub?.ai_generations_remaining;
  if (limit == null || remaining == null) {
    return { blocked: false, remaining: null, needed, plan: sub?.effective_plan };
  }
  return {
    blocked: needed > remaining,
    remaining,
    needed,
    plan: sub?.effective_plan,
  };
}
